import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// How often, and for how long, we look for the anchor. CMS content arrives from
// Strapi after the first render, so the target element does not exist yet when
// the route mounts.
const POLL_INTERVAL_MS = 100;
const POLL_TIMEOUT_MS = 10000;
// Every section above the anchor fetches from Strapi on its own and renders
// lazy-loaded images, so the anchor keeps moving down after the first scroll.
// Rather than guess when that stops, re-scroll whenever it moves and call it
// settled once it has held still for SETTLE_STABLE_TICKS in a row.
const SETTLE_INTERVAL_MS = 100;
const SETTLE_STABLE_TICKS = 5;
const SETTLE_TIMEOUT_MS = 12000;
const HIGHLIGHT_CLASS = "nb-anchor-highlight";
// Matches the nb-anchor-highlight animation length in index.css.
const HIGHLIGHT_DURATION_MS = 1500;

// Anchor ids on this site are mixed case (e.g. "SolutionbyProduct"), but a
// visitor arriving from a redirected product domain may not preserve case.
// Exact match first so existing links behave as before, then a case-insensitive
// fallback so /paperbos, /PaperBOS and /PAPERBOS all resolve.
const findAnchor = (id) => {
  const exact = document.getElementById(id);
  if (exact) return exact;

  const wanted = id.toLowerCase();
  const candidates = Array.from(document.querySelectorAll("[id]"));
  return candidates.find((node) => node?.id?.toLowerCase() === wanted) || null;
};

// A malformed path (e.g. "/%") makes decodeURIComponent throw, which would take
// the whole page down to the ErrorBoundary. Fall back to the raw segment.
const decodeAnchorId = (segment) => {
  try {
    return decodeURIComponent(segment);
  } catch (error) {
    return segment;
  }
};

// The anchor's offset within the document, which changes only when content
// above it resizes — not while the page is scrolling.
const documentTop = (element) =>
  element.getBoundingClientRect().top + window.scrollY;

const Scroller = () => {
  const location = useLocation();
  const cancelledRef = useRef(false);
  const intervalRef = useRef(null);
  const settleIntervalRef = useRef(null);
  const timeoutsRef = useRef([]);
  const elapsedRef = useRef(0);
  const lastDocTopRef = useRef(0);
  const stableTicksRef = useRef(0);
  const settleElapsedRef = useRef(0);
  const highlightedRef = useRef(null);

  useEffect(() => {
    cancelledRef.current = false;
    elapsedRef.current = 0;
    timeoutsRef.current = [];

    const trackTimeout = (callback, delay) => {
      const timeoutId = window.setTimeout(callback, delay);
      timeoutsRef.current.push(timeoutId);
      return timeoutId;
    };

    const stopPolling = () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const stopSettling = () => {
      if (settleIntervalRef.current !== null) {
        window.clearInterval(settleIntervalRef.current);
        settleIntervalRef.current = null;
      }
    };

    // Home restores wherever the visitor left off.
    if (location.pathname.length <= 1) {
      const savedScrollPosition = parseInt(
        localStorage.getItem("scrollPosition"),
        10
      );
      window.scrollTo(
        0,
        Number.isNaN(savedScrollPosition) ? 0 : savedScrollPosition
      );
      return undefined;
    }

    const targetId = decodeAnchorId(location.pathname.substring(1));

    const scrollTo = (element, behavior) =>
      element.scrollIntoView({ behavior, block: "start" });

    const highlight = (element) => {
      highlightedRef.current = element;
      element.classList.add(HIGHLIGHT_CLASS);
      trackTimeout(() => {
        element.classList.remove(HIGHLIGHT_CLASS);
        highlightedRef.current = null;
      }, HIGHLIGHT_DURATION_MS);
    };

    const onFound = (element) => {
      scrollTo(element, "smooth");
      lastDocTopRef.current = documentTop(element);
      stableTicksRef.current = 0;
      settleElapsedRef.current = 0;

      const settle = () => {
        if (cancelledRef.current === true) {
          stopSettling();
          return;
        }

        const currentTop = documentTop(element);
        settleElapsedRef.current += SETTLE_INTERVAL_MS;

        if (currentTop !== lastDocTopRef.current) {
          // Something above the anchor finished loading and pushed it along.
          // Correct instantly so we do not fight the smooth scroll still
          // running from the previous attempt.
          lastDocTopRef.current = currentTop;
          stableTicksRef.current = 0;
          scrollTo(element, "auto");
        } else {
          stableTicksRef.current += 1;
        }

        if (
          stableTicksRef.current >= SETTLE_STABLE_TICKS ||
          settleElapsedRef.current >= SETTLE_TIMEOUT_MS
        ) {
          stopSettling();
          // Highlight once the tile is actually where it will stay, so the
          // grow animation is not wasted mid-flight.
          highlight(element);
        }
      };

      settleIntervalRef.current = window.setInterval(settle, SETTLE_INTERVAL_MS);
    };

    const poll = () => {
      const element = findAnchor(targetId);
      if (element) {
        stopPolling();
        onFound(element);
        return;
      }

      elapsedRef.current += POLL_INTERVAL_MS;
      if (elapsedRef.current >= POLL_TIMEOUT_MS) stopPolling();
    };

    intervalRef.current = window.setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelledRef.current = true;
      stopPolling();
      stopSettling();
      timeoutsRef.current.forEach((timeoutId) =>
        window.clearTimeout(timeoutId)
      );
      timeoutsRef.current = [];
      if (highlightedRef.current !== null) {
        highlightedRef.current.classList.remove(HIGHLIGHT_CLASS);
        highlightedRef.current = null;
      }
    };
  }, [location.pathname]);

  return <></>;
};

export default Scroller;
